import { gql } from '@apollo/client'
import { useLazyQuery, useMutation } from '@apollo/client/react'
import DownloadIcon from '@mui/icons-material/Download'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useCallback, useEffect } from 'react'
import { useAuthProvider } from 'src/context/AuthProvider'
import useLogger from 'src/context/LoggerProvider/useLogger'
import {
  CustomerType,
  ILocation,
  IMutation,
  IMutationSendCustomerInfoEmailArgs,
  Interest,
  IQuery,
  IQueryLocationByConfigurationArgs
} from 'src/types/graphql'
import { RedirectUrls } from 'src/types/types'
import useAddToCart from 'src/utils/pixels/common'
import ga from 'src/utils/pixels/gtag'

const SEND_CUSTOMER_INFO_EMAIL = gql`
  mutation SendCustomerInfoEmail($customerInfo: CustomerInfoInput!) {
    sendCustomerInfoEmail(customerInfo: $customerInfo)
  }
`

const GET_LOCATION_CONFIG = gql`
  query GetLocationConfig($configurationId: String!) {
    locationByConfiguration(configurationId: $configurationId) {
      technicalReport {
        id
        url
        name
      }
    }
  }
`

type IProps = {
  location: ILocation
  configurationId: string | null | undefined
  isBrokerMode: boolean
  reportsRef: React.RefObject<HTMLDivElement | null>
}

export default function Reports({
  location,
  configurationId,
  isBrokerMode,
  reportsRef
}: IProps) {
  const { t } = useTranslation('details')
  const { hasTechnicalReport, urlRoute, defaultConfiguration } = location
  const { locationType } = defaultConfiguration ?? {}

  const handleAddToCart = useAddToCart()

  const { currentUser, toggleLoginModal } = useAuthProvider()
  const router = useRouter()
  const logger = useLogger()

  const [createProLead] = useMutation<
    Pick<IMutation, 'sendCustomerInfoEmail'>,
    IMutationSendCustomerInfoEmailArgs
  >(SEND_CUSTOMER_INFO_EMAIL, {
    onError: ({ message }) => {
      logger.error(`Error creating lead`, { message })
    }
  })

  const handleCreateProLead = useCallback(() => {
    const { firstname, lastname, email, phoneNumber, userChanges } =
      currentUser ?? {}
    createProLead({
      variables: {
        customerInfo: {
          name: firstname ?? userChanges?.firstname ?? '',
          lastname: lastname ?? userChanges?.lastname ?? '',
          email: email ?? userChanges?.email ?? '',
          phone: phoneNumber ?? userChanges?.phoneNumber ?? '',
          interest: Interest.Rent,
          customerType: CustomerType.RentBroker,
          locationId: location.id,
          configId: defaultConfiguration?.id,
          leadSource: 'listing_technical_report'
        }
      }
    })
  }, [createProLead, currentUser, location.id, defaultConfiguration?.id])

  const [
    getLocationConfig,
    { data: locationConfigData, error: locationConfigError, loading }
  ] = useLazyQuery<
    Pick<IQuery, 'locationByConfiguration'>,
    IQueryLocationByConfigurationArgs
  >(GET_LOCATION_CONFIG)

  useEffect(() => {
    if (locationConfigError) {
      logger.error('Error fetching location config', locationConfigError)
    }
  }, [locationConfigError, logger])

  useEffect(() => {
    if (locationConfigData?.locationByConfiguration) {
      const loc = locationConfigData.locationByConfiguration
      if (!loc.technicalReport?.url) return
      router.push(loc.technicalReport.url)
      handleCreateProLead()
    }
  }, [locationConfigData, handleCreateProLead, router])

  if (!configurationId) return null
  if (!isBrokerMode) return null

  const handleDownload = () => {
    handleAddToCart('technical_report', location, defaultConfiguration)

    if (!currentUser) {
      toggleLoginModal(true, 'report')
      return
    }
    if (!currentUser?.isVerified && currentUser?.requestedVerification) {
      router.push(
        RedirectUrls.UserConfirmation +
          `?redirectU=${encodeURIComponent(location.urlRoute ?? '')}`
      )
      return
    }

    ga.event('download_technical_report', {
      locationType: locationType,
      city: location.addressDetails?.city
    })
    if (hasTechnicalReport) {
      getLocationConfig({ variables: { configurationId } })
    } else {
      window.open(`${urlRoute}/report`, '_blank')
      handleCreateProLead()
    }
  }

  return (
    <>
      <hr
        className="mt-8 h-px w-full border-0 bg-ink-100/15 md:mt-12"
        aria-hidden="true"
      />
      <section className="mt-8 md:mt-12" ref={reportsRef}>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-12">
          <div className="md:col-span-5">
            <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
              {t('REPORTS_SECTION.EYEBROW')}
            </p>
            <h3 className="mt-3 font-display text-[20px] leading-tight font-medium text-ink-700 md:text-[24px]">
              {t('REPORTS_SECTION.TITLE')}
            </h3>
          </div>
          <div className="md:col-span-7">
            <p className="max-w-md text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
              {t('REPORTS_SECTION.DESCRIPTION')}
            </p>
            <button
              type="button"
              onClick={handleDownload}
              disabled={loading}
              className="mt-6 inline-flex items-center gap-2 border border-ink-700 bg-ink-700 px-6 py-3 text-[12px] font-medium tracking-[0.14em] text-white-500 uppercase transition-colors hover:border-blue-500 hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? (
                <span className="animate-pulse">
                  {t('REPORTS_SECTION.LOADING')}
                </span>
              ) : (
                <>
                  <DownloadIcon className="!text-[16px]" />
                  {t('REPORTS_SECTION.DOWNLOAD')}
                </>
              )}
            </button>
          </div>
        </div>
      </section>
    </>
  )
}
